import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { SalesStatCard, SalesLeaderboard, SalesPagination } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { FilterBar, FilterFieldConfig } from '../../../components/FilterBar/FilterBar';
import { SalesRepItem, ITEMS_PER_PAGE } from './salesTypes';
import { getSalesLeaderboard, SalesLeaderboard as ApiLeaderboard } from '../../../services/salesAnalyticsApi';
import styles from './AdminSales.module.css';

// Note (Sec 12): GET /sales/leaderboard has no server-side filter params —
// unlike the Accounting ledger, this batch's backend work didn't add any to
// the sales endpoints. This search is a client-side filter over the already
// -fetched leaderboard so the page still gets a working filter UI; it is not
// a server round-trip like the Accounting ledger's filters are.
const SALES_FILTER_FIELDS: FilterFieldConfig[] = [
  { key: 'search', type: 'text', label: 'Search reps', placeholder: 'Search reps by name…' },
];

function fmtMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();
}

const AdminSales: React.FC = () => {
  const [data, setData] = useState<ApiLeaderboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    getSalesLeaderboard()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load sales leaderboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const reps = useMemo<SalesRepItem[]>(() =>
    (data?.reps ?? []).map((r, i) => ({
      id: String(r.rep_id),
      rank: i + 1,
      initials: initials(r.rep_name),
      name: r.rep_name,
      revenue: r.revenue_mtd,
      marginPercent: r.margin_percent,
      customers: r.customer_count,
      target: r.target_revenue,
      achievement: r.achievement_percent,
      commission: r.commission,
    }))
  , [data]);

  const filteredReps = useMemo(() => {
    if (!search.trim()) return reps;
    const q = search.trim().toLowerCase();
    return reps.filter((r) => r.name.toLowerCase().includes(q));
  }, [reps, search]);

  const totalPages = Math.max(Math.ceil(filteredReps.length / ITEMS_PER_PAGE), 1);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredReps.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredReps, currentPage]);

  const stats = data?.stats ?? { total_sales_mtd: 0, orders_mtd: 0, sales_reps_count: 0, avg_deal_size: 0 };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !data}
        error={error}
        empty={false}
        onRetry={load}
      >
        <div className={styles.statsGrid}>
          <SalesStatCard title="Total Sales MTD" value={fmtMoney(stats.total_sales_mtd)} icon="sales" color="blue" />
          <SalesStatCard title="Orders MTD" value={String(stats.orders_mtd)} icon="orders" color="teal" />
          <SalesStatCard title="Sales Reps" value={String(stats.sales_reps_count)} icon="reps" color="orange" />
          <SalesStatCard title="Avg Deal Size" value={fmtMoney(stats.avg_deal_size)} icon="deal" color="green" />
        </div>

        <FilterBar
          fields={SALES_FILTER_FIELDS}
          values={{ search }}
          onChange={(_key, value) => { setSearch(value); setCurrentPage(1); }}
          onClear={() => { setSearch(''); setCurrentPage(1); }}
        />

        <SalesLeaderboard items={paginatedItems} />

        {totalPages > 1 && (
          <SalesPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </AsyncBoundary>
    </div>
  );
};

export const AdminSalesPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminSales />
  </ProtectedRoute>
);

export { AdminSales };
export default AdminSales;
